import React, { useState, useRef, useEffect } from 'react';
import './TripForm.css';

const TripForm = ({ vehicles, onClose, trip }) => {
  const [formData, setFormData] = useState({
    vehicle_id: '',
    action: 'BOARD',
    rtkm: '',
    hsd: '',
    advance_exp: '',
    destination: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Searchable dropdown states
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVehicleSelect = (vehicle) => {
    setFormData(prev => ({
      ...prev,
      vehicle_id: vehicle.id
    }));
    setSearchTerm(`${vehicle.vehicle_number} - ${vehicle.driver_name}`);
    setIsDropdownOpen(false);
  };

  // Filter vehicles based on search term
  const filteredVehicles = vehicles.filter(vehicle => {
    const searchString = `${vehicle.vehicle_number} ${vehicle.driver_name} ${vehicle.current_status || ''}`.toLowerCase();
    return searchString.includes(searchTerm.toLowerCase());
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/trips.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('Trip recorded successfully!');
        setTimeout(() => {
          onClose(true);
        }, 1500);
      } else {
        setError(data.message || 'Failed to record trip');
      }
    } catch (error) {
      setError('Error connecting to server');
      console.error('Submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="trip-form"
      style={{
        maxHeight: '85vh',
        overflowY: 'auto',
        padding: '20px',
        boxSizing: 'border-box'
      }}
    >
      <div className="form-header" style={{ position: 'sticky', top: 0, background: 'white', zIndex: 10, paddingBottom: '10px' }}>
        <h2>Record New Trip</h2>
        <button className="btn-close" onClick={() => onClose(false)}>×</button>
      </div>

      {error && (
        <div className="alert alert-danger">
          <span>❌</span> {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>✅</span> {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Searchable Vehicle Dropdown */}
        <div className="form-group" ref={dropdownRef}>
          <label htmlFor="vehicle-search">
            Select Vehicle *
            <span className="hint">Type to search by vehicle number or driver name</span>
          </label>
          
          <div className="searchable-select">
            <input
              type="text"
              id="vehicle-search"
              placeholder="Search vehicle number or driver..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setIsDropdownOpen(true);
              }}
              onFocus={() => setIsDropdownOpen(true)}
              disabled={loading}
              className="vehicle-search-input"
              autoComplete="off"
            />

            {isDropdownOpen && (
              <div className="vehicle-dropdown">
                {filteredVehicles.length > 0 ? (
                  filteredVehicles.map(vehicle => (
                    <div
                      key={vehicle.id}
                      className={`vehicle-option ${formData.vehicle_id === vehicle.id ? 'selected' : ''}`}
                      onClick={() => handleVehicleSelect(vehicle)}
                    >
                      <strong>{vehicle.vehicle_number}</strong> - {vehicle.driver_name}
                      <span className="vehicle-status">({vehicle.current_status})</span>
                    </div>
                  ))
                ) : (
                  <div className="no-results">No matching vehicles found</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="form-group">
          <label htmlFor="action">
            Action *
            <span className="hint">Boarding or Unboarding operation</span>
          </label>
          <div className="action-buttons">
            <button
              type="button"
              className={`action-btn ${formData.action === 'BOARD' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, action: 'BOARD' }))}
              disabled={loading}
            >
              <span>✅</span> BOARD
            </button>
            <button
              type="button"
              className={`action-btn ${formData.action === 'UNBOARD' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, action: 'UNBOARD' }))}
              disabled={loading}
            >
              <span>⏸️</span> UNBOARD
            </button>
          </div>
        </div>

        {/* Other Fields */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="rtkm">RTKM *</label>
            <input
              type="number"
              id="rtkm"
              name="rtkm"
              value={formData.rtkm}
              onChange={handleChange}
              placeholder="e.g. 245"
              step="1"
              min="0"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="hsd">HSD (liters)</label>
            <input
              type="number"
              id="hsd"
              name="hsd"
              value={formData.hsd}
              onChange={handleChange}
              placeholder="e.g. 42.5"
              step="0.1"
              min="0"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="advance_exp">Advance Exp (₹)</label>
            <input
              type="number"
              id="advance_exp"
              name="advance_exp"
              value={formData.advance_exp}
              onChange={handleChange}
              placeholder="e.g. 1500"
              step="1"
              min="0"
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="destination">Destination</label>
          <input
            type="text"
            id="destination"
            name="destination"
            value={formData.destination}
            onChange={handleChange}
            placeholder="Where the trip ends"
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="notes">
            Notes
            <span className="hint">Additional information about this trip</span>
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Enter any notes or observations..."
            rows="5"
            disabled={loading}
            style={{ width: '100%', minHeight: '100px' }}
          />
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary" 
            onClick={() => onClose(false)}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-primary" 
            disabled={loading || !formData.vehicle_id}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Recording...
              </>
            ) : 'Record Trip'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TripForm;